import { useCallback, useMemo, useState } from "react"
import { MenuItem } from "@mui/material"
import { useSearchParams } from "react-router-dom"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"

import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { ApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useColumnFactory } from "../../../../../core/api/useTableColumns"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { SERVICE_CENTER_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    ReminderType,
    ResourceNotification,
    ResourceNotificationForm,
} from "../../organization/resources/resourcesTypes"
import { AddReminderDialog } from "../../organization/resources/components/room/general/AddReminderDialog"
import { getReminderTimeLabel } from "../../organization/resources/components/room/general/utils"

// TODO: reminders are kept in local state only — there is no shared-reminders API yet. Wire up
// persistence (list/create/update/delete shared across resources) once the backend is available.
export function ServiceCenterReminders() {
    const { l10n } = useAppLocalization()
    const [searchParams] = useSearchParams()

    const [reminders, setReminders] = useState<ResourceNotification[]>([])
    const [editNotification, setEditNotification] = useState<ResourceNotification | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(searchParams.get("create") === "true")

    const openCreate = () => {
        setEditNotification(null)
        setIsDialogOpen(true)
    }

    const handleAdd = (value: ResourceNotificationForm) => {
        const newItem: ResourceNotification = {
            ...value,
            receiverType: "",
            messageType: "",
            isActive: true,
        }
        setReminders((prev) =>
            editNotification
                ? prev.map((item) => (item.id === editNotification.id ? newItem : item))
                : [...prev, newItem],
        )
        setEditNotification(null)
        setIsDialogOpen(false)
    }

    const columnFactory = useColumnFactory<ResourceNotification>()
    const columns = useMemo<MRT_ColumnDef<ResourceNotification>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-type"),
                accessorKey: "type",
                accessorFn: (row) => (
                    <LocalizedEnum base="reminder-type-option" enumValue={row.type}>
                        <span>{row.type}</span>
                    </LocalizedEnum>
                ),
            }),
            columnFactory.nameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-sent"),
                accessorFn: (row) =>
                    getReminderTimeLabel(row.sendInMinutesBeforeMeeting, row.sendInMinutesAfterMeeting),
            }),
            columnFactory.nameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-content"),
                accessorKey: "message",
                accessorFn: (row) =>
                    row.type === ReminderType.MEETING_CONFIRMATION
                        ? l10n.getString("service-center-reminders-table-confirmation-content")
                        : row.message,
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ closeMenu, row }: { row: MRT_Row<ResourceNotification>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    setEditNotification(row.original)
                    setIsDialogOpen(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-edit-action">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setReminders((prev) => prev.filter((item) => item.id !== row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-delete-action">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="service-center-reminders-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_REMINDERS_ROUTE}
                    title="Reminders"
                    rightElement={
                        <LocalizedStrict id="service-center-reminders-create-button">
                            <StandardButton onClick={openCreate}>Create reminder</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="service-center-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-communication">
                            <Breadcrumb>Communication</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-communication-reminders">
                            <Breadcrumb>Reminders</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                isLoading={false}
                isFetching={false}
                isError={false}
                data={reminders}
                columns={columns}
                initialState={{ density: "spacious" }}
                enableRowActions
                renderRowActionMenuItems={renderRowActionMenuItems}
                positionActionsColumn="last"
            />

            {isDialogOpen && (
                <AddReminderDialog
                    open={isDialogOpen}
                    notification={editNotification}
                    notifications={reminders}
                    onClose={() => {
                        setEditNotification(null)
                        setIsDialogOpen(false)
                    }}
                    onAdd={handleAdd}
                />
            )}
        </>
    )
}
